import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { formatDateOnly, formatDisplayDate, getDateAtIndex, usePushParams, parseCrawlDate } from "./commons";
import { MdSkipNext, MdSkipPrevious, MdChevronLeft, MdChevronRight } from "react-icons/md";

const DAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

function CalendarPicker({ changeDateSet, selectedDate, onSelect, minDateStr, maxDateStr }) {
    const init = selectedDate ? new Date(selectedDate + 'T00:00:00') : new Date();
    const [year, setYear] = useState(init.getFullYear());
    const [month, setMonth] = useState(init.getMonth());

    const prevMonth = () => month === 0 ? (setMonth(11), setYear(y => y - 1)) : setMonth(m => m - 1);
    const nextMonth = () => month === 11 ? (setMonth(0), setYear(y => y + 1)) : setMonth(m => m + 1);

    const viewMonthStr = `${year}-${String(month + 1).padStart(2, '0')}`;
    const minMonthStr  = minDateStr ? minDateStr.slice(0, 7) : '0000-00';
    const maxMonthStr  = maxDateStr ? maxDateStr.slice(0, 7) : '9999-99';
    const canPrev = viewMonthStr > minMonthStr;
    const canNext = viewMonthStr < maxMonthStr;

    const firstDow = (new Date(year, month, 1).getDay() + 6) % 7;
    const lastDay  = new Date(year, month + 1, 0).getDate();

    const cells = useMemo(() => {
        const out = Array(firstDow).fill(null);
        for (let d = 1; d <= lastDay; d++) out.push(d);
        return out;
    }, [year, month]);

    return (
        <>
            <CalHeader>
                <CalNavBtn onClick={prevMonth} disabled={!canPrev}>‹</CalNavBtn>
                <CalMonthTitle>{MONTHS[month]} {year}</CalMonthTitle>
                <CalNavBtn onClick={nextMonth} disabled={!canNext}>›</CalNavBtn>
            </CalHeader>
            <DayHeaders>
                {DAYS.map(d => <DayHead key={d}>{d}</DayHead>)}
            </DayHeaders>
            <DayGrid>
                {cells.map((d, i) => {
                    if (d === null) return <DayCell key={`blank-${i}`} $empty />;
                    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                    const hasChange = changeDateSet.has(dateStr);
                    const isSelected = dateStr === selectedDate;
                    const isDisabled = (minDateStr && dateStr < minDateStr) || (maxDateStr && dateStr > maxDateStr);
                    return (
                        <DayCell key={d}
                            $selected={isSelected}
                            $hasChange={hasChange && !isSelected}
                            $disabled={isDisabled}
                            title={hasChange ? 'Changes detected' : undefined}
                            onClick={() => !isDisabled && onSelect(dateStr)}
                        >
                            {d}
                            {hasChange && <ChangeDot $onSelected={isSelected} />}
                        </DayCell>
                    );
                })}
            </DayGrid>
        </>
    );
}

export default function Buttons() {
    const location = useLocation();
    const pushParams = usePushParams();
    const [calOpen, setCalOpen] = useState(false);
    const [calPos, setCalPos] = useState({ top: 0, left: 0 });
    const calRef = useRef(null);
    const triggerRef = useRef(null);
    const { numDaysHistory, pageChanges, curSnapshotDate, lastSnapshotDate } = useSelector(s => s.currentMonitoredUri);

    const todayStr = formatDateOnly(getDateAtIndex(0));

    const { viewStart, viewEnd } = useMemo(() => {
        const p = new URLSearchParams(location.search);
        return { viewStart: p.get('from') || null, viewEnd: p.get('to') || null };
    }, [location.search]);

    // Sorted list of UTC dates with changes, newest first — used for prev/next navigation and URL param
    const changeDates = useMemo(() => {
        const dates = new Set();
        (pageChanges || []).forEach(pc => { if (pc.crawlDate) dates.add(formatDateOnly(parseCrawlDate(pc.crawlDate))); });
        return [...dates].sort((a, b) => b.localeCompare(a));
    }, [pageChanges]);

    const changeDateSet = useMemo(() => new Set(changeDates), [changeDates]);

    const selectedDate = curSnapshotDate || todayStr;

    const changeIdx = changeDates.indexOf(selectedDate);

    const canFirst = changeDates.length > 0 && selectedDate !== changeDates[changeDates.length - 1];
    const canPrev  = changeDates.length > 0 && (changeIdx === -1 || changeIdx < changeDates.length - 1);
    const canNext  = changeDates.length > 0 && (changeIdx === -1 || changeIdx > 0);
    const canLast  = changeDates.length > 0 && selectedDate !== changeDates[0];

    // Select a date: push ?date= to URL
    const select = useCallback((dateStr) => {
        pushParams({ date: dateStr });
    }, [pushParams]);

    const selectSnapshotByDate = (dateStr) => { select(dateStr); setCalOpen(false); };

    function goFirst() { if (changeDates.length) select(changeDates[changeDates.length - 1]); }
    function goLast()  { if (changeDates.length) select(changeDates[0]); }

    function goPrev() {
        if (!changeDates.length) return;
        if (changeIdx === -1) {
            const older = changeDates.find(d => d < selectedDate);
            if (older) select(older);
        } else if (changeIdx < changeDates.length - 1) {
            select(changeDates[changeIdx + 1]);
        }
    }

    function goNext() {
        if (!changeDates.length) return;
        if (changeIdx === -1) {
            const newer = [...changeDates].reverse().find(d => d > selectedDate);
            if (newer) select(newer);
        } else if (changeIdx > 0) {
            select(changeDates[changeIdx - 1]);
        }
    }

    // Latest: remove explicit ?date= (absence = latest).
    // Also reset the view range if latest falls outside it.
    function goLatest() {
        if (!lastSnapshotDate) return;
        const updates = { date: null };
        if (viewStart || viewEnd) {
            const outsideRange =
                (viewStart && lastSnapshotDate < viewStart) ||
                (viewEnd   && lastSnapshotDate > viewEnd);
            if (outsideRange) {
                updates.from = null;
                updates.to   = null;
            }
        }
        pushParams(updates);
    }

    const isOnLatest = curSnapshotDate === lastSnapshotDate;

    const minDateStr = useMemo(() => formatDateOnly(getDateAtIndex(numDaysHistory - 1)), [numDaysHistory]);
    const maxDateStr = todayStr;

    const openCalendar = useCallback(() => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setCalPos({ top: rect.bottom + 8, left: rect.left + rect.width / 2 });
        }
        setCalOpen(o => !o);
    }, []);

    useEffect(() => {
        if (!calOpen) return;
        const handler = e => {
            if (calRef.current && !calRef.current.contains(e.target) &&
                triggerRef.current && !triggerRef.current.contains(e.target)) {
                setCalOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [calOpen]);

    const calendar = calOpen ? ReactDOM.createPortal(
        <CalWrap ref={calRef} style={{ top: calPos.top, left: calPos.left }}>
            <CalendarPicker
                changeDateSet={changeDateSet}
                selectedDate={selectedDate}
                onSelect={selectSnapshotByDate}
                minDateStr={minDateStr}
                maxDateStr={maxDateStr}
            />
        </CalWrap>,
        document.body
    ) : null;

    return (
        <>
        <NavWrap>
            <NavGroup>
                <NavBtn title="First change" $disabled={!canFirst} onClick={goFirst}>
                    <MdSkipPrevious />
                </NavBtn>
                <NavBtn title="Previous change" $disabled={!canPrev} onClick={goPrev}>
                    <MdChevronLeft />
                </NavBtn>

                <DateLabelWrap>
                    <DateLabel ref={triggerRef} onClick={openCalendar} title="Pick a date">
                        {formatDisplayDate(selectedDate)}
                    </DateLabel>
                </DateLabelWrap>

                <NavBtn title="Next change" $disabled={!canNext} onClick={goNext}>
                    <MdChevronRight />
                </NavBtn>
                <NavBtn title="Last change" $disabled={!canLast} onClick={goLast}>
                    <MdSkipNext />
                </NavBtn>

                <NavDivider />

                <LatestBtn
                    title="Go to latest crawl"
                    $active={isOnLatest}
                    onClick={goLatest}
                >
                    Latest
                </LatestBtn>
            </NavGroup>
        </NavWrap>
        {calendar}
        </>
    );
}

const NavWrap = styled.div`
  display: flex;
  justify-content: center;
`;

const NavGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 4px;
`;

const NavBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 7px;
  border: none;
  background: ${({ $disabled }) => $disabled ? 'transparent' : '#fff'};
  color: ${({ $disabled }) => $disabled ? '#cbd5e1' : '#0f172a'};
  cursor: ${({ $disabled }) => $disabled ? 'default' : 'pointer'};
  font-size: 1.1rem;
  transition: background 120ms, color 120ms;
  box-shadow: ${({ $disabled }) => $disabled ? 'none' : '0 1px 3px rgba(0,0,0,0.08)'};
  &:hover { background: ${({ $disabled }) => $disabled ? 'transparent' : '#ede9fe'};
            color: ${({ $disabled }) => $disabled ? '#cbd5e1' : '#6366f1'}; }
`;

const NavDivider = styled.div`
  width: 1px;
  height: 20px;
  background: #e2e8f0;
  margin: 0 2px;
`;

const LatestBtn = styled.button`
  padding: 0 10px;
  height: 32px;
  border-radius: 7px;
  border: none;
  background: ${({ $active }) => $active ? '#ede9fe' : '#fff'};
  color: ${({ $active }) => $active ? '#6366f1' : '#64748b'};
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 120ms, color 120ms;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  &:hover { background: #ede9fe; color: #6366f1; }
`;

const DateLabelWrap = styled.div`
  position: relative;
`;

const DateLabel = styled.span`
  display: inline-block;
  font-size: 0.78rem;
  font-weight: 600;
  color: #0f172a;
  font-family: "Inter", system-ui, sans-serif;
  padding: 6px 10px;
  min-width: 110px;
  text-align: center;
  font-variant-numeric: tabular-nums;
  cursor: pointer;
  border-radius: 7px;
  transition: background 120ms, color 120ms;
  &:hover { background: #ede9fe; color: #6366f1; }
`;

const CalWrap = styled.div`
  position: fixed;
  transform: translateX(-50%);
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  padding: 12px;
  width: 224px;
  z-index: 9999;
  font-family: "Inter", system-ui, sans-serif;
`;

const CalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const CalMonthTitle = styled.span`
  font-size: 0.78rem;
  font-weight: 700;
  color: #0f172a;
`;

const CalNavBtn = styled.button`
  width: 24px;
  height: 24px;
  border: none;
  background: none;
  border-radius: 5px;
  cursor: ${({ disabled }) => disabled ? 'default' : 'pointer'};
  color: ${({ disabled }) => disabled ? '#cbd5e1' : '#64748b'};
  font-size: 1rem;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:hover:not(:disabled) { background: #f1f5f9; color: #0f172a; }
`;

const DayHeaders = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  margin-bottom: 4px;
`;

const DayHead = styled.div`
  font-size: 0.65rem;
  font-weight: 600;
  color: #94a3b8;
  text-align: center;
  padding: 2px 0;
`;

const DayGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
`;

const DayCell = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  border-radius: 6px;
  font-size: 0.72rem;
  font-weight: ${({ $selected }) => $selected ? 700 : 400};
  background: ${({ $selected }) => $selected ? '#6366f1' : 'transparent'};
  color: ${({ $selected, $disabled, $empty }) =>
    $empty ? 'transparent' :
    $selected ? '#fff' :
    $disabled ? '#cbd5e1' : '#0f172a'};
  cursor: ${({ $disabled, $empty }) => ($disabled || $empty) ? 'default' : 'pointer'};
  transition: background 100ms;
  ${({ $hasChange }) => $hasChange && 'font-weight: 600;'}
  &:hover {
    background: ${({ $selected, $disabled, $empty }) =>
      ($selected || $disabled || $empty) ? undefined : '#ede9fe'};
    color: ${({ $selected, $disabled, $empty }) =>
      ($selected || $disabled || $empty) ? undefined : '#6366f1'};
  }
`;

const ChangeDot = styled.span`
  position: absolute;
  bottom: 3px;
  left: 50%;
  transform: translateX(-50%);
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: ${({ $onSelected }) => $onSelected ? '#fff' : '#6366f1'};
`;
